/*
 * Address
 *
 * Author: Job Ouddeken
 *
 * Description: Fetches streetname and city from a GDL Api when a valid
 * postal code and street number are filled in. All settings are in the
 * this.config object below. The class makes use of a exists() function to check
 * if the assigned classSelector exists on the page. If not it will return
 * and stop. It also uses a setState function to keep track of internal state
 * of the class.
 *
 * Improvements:
 *  - Settings should come from a json file in the src/data folder.
 *
 * Dependencies:
 *  - jQuery
 *  - Base
 */

import $ from 'jquery';
import Base from '../core/Base';

class Address extends Base {
  constructor() {
    super();
    this.config = {
      classSelector: '.js-address',
      toggleClass: 'is-display-none',
      validClass: 'valid',
      invalidClass: 'input-validation-error',
      postalCode: {
        input: 'input[name=postal-code]',
        pattern: /^[0-9]{4} ?[a-zA-Z]{2}$/,
      },
      houseNumber: {
        input: 'input[name=house-number]',
        pattern: /^\d+$/,
      },
      street: {
        input: '.js-street-name',
      },
      city: {
        input: '.js-city',
      },
      success: {
        wrapper: '.js-address-found--wrapper',
        street: '.js-address-found--street',
        city: '.js-address-found--city',
      },
      error: {
        wrapper: '.js-address-error--wrapper',
      },
      api: {
        endpoint: '/gdl-api/addresses',
        tokenElementName: '__RequestVerificationToken',
      },
    };
    this.state = {
      cityValue: null,
      houseNumberValue: null,
      postalCodeValue: null,
      streetNameValue: null,
      token: this.getToken(),
    };
    this.init();
  }

  init() {
    const selector = $(this.config.classSelector);
    if (!this.exists(selector)) return;
    this.setState(
      {
        selector,
      },
      () => this.selectElements()
    );
  }

  selectElements() {
    const { selector } = this.state;
    const postalCodeInput = selector.find(this.config.postalCode.input);
    const houseNumberInput = selector.find(this.config.houseNumber.input);
    const successWrapper = selector.find(this.config.success.wrapper);
    const successStreet = selector.find(this.config.success.street);
    const successCity = selector.find(this.config.success.city);
    const lookupStreet = selector.find(this.config.street.input);
    const lookupCity = selector.find(this.config.city.input);
    const errorWrapper = selector.find(this.config.error.wrapper);
    const postalCodeValue = postalCodeInput.val();
    const houseNumberValue = houseNumberInput.val();

    this.setState(
      {
        postalCodeInput,
        houseNumberInput,
        successWrapper,
        successStreet,
        successCity,
        lookupStreet,
        lookupCity,
        errorWrapper,
        postalCodeValue,
        houseNumberValue,
      },
      () => this.setEvents()
    );
  }

  setEvents() {
    const { postalCodeInput, houseNumberInput } = this.state;

    postalCodeInput.on('keyup', event => {
      this.setState({ postalCodeValue: event.currentTarget.value });
    });
    houseNumberInput.on('keyup', event => {
      this.setState({ houseNumberValue: event.currentTarget.value });
    });
    postalCodeInput.on('blur', () => this.validate());
    houseNumberInput.on('blur', () => this.validate());
  }

  validate() {
    const { postalCodeValue, houseNumberValue } = this.state;
    const { postalCode, houseNumber } = this.config;

    const postCodeCheck = postalCode.pattern.test(postalCodeValue);
    const houseNumberCheck = houseNumber.pattern.test(houseNumberValue);

    if (postCodeCheck && houseNumberCheck) {
      this.requestAddress();
    }
  }

  requestAddress() {
    const { postalCodeValue, houseNumberValue, token } = this.state;
    const url = this.constructApiUrl(postalCodeValue, houseNumberValue);

    window.disableSubmitButton();
    $.post(url, { __RequestVerificationToken: token })
      .done(data => {
        const { city, streetName } = data;
        this.setState(
          {
            cityValue: city,
            streetNameValue: streetName,
          },
          () => this.success()
        );
        window.enableSubmitButton();
      })
      .fail(() => {
        this.setState(
          {
            cityValue: null,
            streetNameValue: null,
          },
          () => this.error()
        );
        window.enableSubmitButton();
      });
  }

  success() {
    const { toggleClass, validClass, invalidClass } = this.config;
    const { postalCodeInput, houseNumberInput, streetNameValue, cityValue, successWrapper, successStreet, successCity, lookupStreet, lookupCity, errorWrapper } = this.state;

    lookupStreet.val(streetNameValue);
    lookupCity.val(cityValue);
    successStreet.html(streetNameValue);
    successCity.html(cityValue);

    // Could be improved by changing the HTML and CSS setup
    postalCodeInput.removeClass(invalidClass);
    postalCodeInput.addClass(validClass);
    houseNumberInput.removeClass(invalidClass);
    houseNumberInput.addClass(validClass);
    successWrapper.removeClass(toggleClass);
    errorWrapper.addClass(toggleClass);
  }

  error() {
    const { toggleClass, validClass, invalidClass } = this.config;
    const { postalCodeInput, houseNumberInput, successWrapper, successStreet, successCity, lookupStreet, lookupCity, errorWrapper } = this.state;

    lookupStreet.val('');
    lookupCity.val('');
    successStreet.html();
    successCity.html();

    // Could be improved by changing the HTML and CSS setup
    postalCodeInput.removeClass(validClass);
    postalCodeInput.addClass(invalidClass);
    houseNumberInput.removeClass(validClass);
    houseNumberInput.addClass(invalidClass);
    successWrapper.addClass(toggleClass);
    errorWrapper.removeClass(toggleClass);
  }

  getToken() {
    let token;
    if (document.getElementsByName(this.config.api.tokenElementName)[0]) {
      token = document.getElementsByName(this.config.api.tokenElementName)[0].value;
    }
    return token;
  }

  constructApiUrl(postalCode, houseNumber) {
    const { endpoint } = this.config.api;
    return `${endpoint}/${postalCode}/${houseNumber}`;
  }
}

export default Address;
